import DataTable from "app/components/DataTable";
import { CheckedIcon } from "app/icons";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ManufacturerModelRow } from "../../types";
import { useStyles } from "./styles";

type Props = {
  rows: ManufacturerModelRow[];
  loading?: boolean;
};

const CHECKBOX_SIZE = 20;

const CHECKBOX_COLUMNS: Array<{
  id: keyof ManufacturerModelRow;
  labelKey: string;
}> = [
  { id: "sideLighting", labelKey: "SIDE_LIGHTING" },
  { id: "sideGuard", labelKey: "SIDE_GUARD" },
  { id: "mudguard", labelKey: "MUDGUARD" },
  { id: "extendableChassis", labelKey: "EXTENDABLE_CHASSIS" },
];

const renderCheckbox = (styles: ReturnType<typeof useStyles>) => (
  <span className={styles.checkboxCell}>
    <CheckedIcon width={CHECKBOX_SIZE} height={CHECKBOX_SIZE} />
  </span>
);

const formatCellValue = (value: string | number | null): string | number => {
  if (value === null || value === "") {
    return "-";
  }

  return value;
};

const renderValue = (
  value: string | number | null,
  styles: ReturnType<typeof useStyles>
) => (
  <span className={styles.textCell}>{formatCellValue(value)}</span>
);

const ModelTable = ({ rows, loading = false }: Props) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        id: "manufacturerName",
        label: t("MANUFACTURER_MODELS.TABLE.MANUFACTURER"),
        align: "center",
        minWidth: 100,
        format: (row: ManufacturerModelRow) => renderValue(row.manufacturerName, styles),
      },
      {
        id: "modelDesc",
        label: t("MANUFACTURER_MODELS.TABLE.MODEL"),
        align: "center",
        minWidth: 180,
        format: (row: ManufacturerModelRow) => renderValue(row.modelDesc, styles),
      },
      {
        id: "year",
        label: t("MANUFACTURER_MODELS.TABLE.YEAR"),
        align: "center",
        minWidth: 72,
        format: (row: ManufacturerModelRow) => renderValue(row.year, styles),
      },
      {
        id: "chassisWidth",
        label: t("MANUFACTURER_MODELS.TABLE.CHASSIS_WIDTH"),
        align: "center",
        minWidth: 96,
        format: (row: ManufacturerModelRow) => renderValue(row.chassisWidth, styles),
      },
      {
        id: "chassisHeight",
        label: t("MANUFACTURER_MODELS.TABLE.CHASSIS_HEIGHT"),
        align: "center",
        minWidth: 96,
        format: (row: ManufacturerModelRow) => renderValue(row.chassisHeight, styles),
      },
      {
        id: "baseBeamType",
        label: t("MANUFACTURER_MODELS.TABLE.OPENING_BASE"),
        align: "center",
        minWidth: 96,
        format: (row: ManufacturerModelRow) => renderValue(row.baseBeamType, styles),
      },
      {
        id: "maxWidth",
        label: t("MANUFACTURER_MODELS.TABLE.TRACK_WIDTH"),
        align: "center",
        minWidth: 96,
        format: (row: ManufacturerModelRow) => renderValue(row.maxWidth, styles),
      },
      {
        id: "frontExtension",
        label: t("MANUFACTURER_MODELS.TABLE.FRONT_OVERHANG"),
        align: "center",
        minWidth: 110,
        format: (row: ManufacturerModelRow) => renderValue(row.frontExtension, styles),
      },
      {
        id: "licenseWeight",
        label: t("MANUFACTURER_MODELS.TABLE.LICENSED_WEIGHT"),
        align: "center",
        minWidth: 110,
        format: (row: ManufacturerModelRow) => renderValue(row.licenseWeight, styles),
      },
      {
        id: "weelDrive",
        label: t("MANUFACTURER_MODELS.TABLE.DRIVE"),
        align: "center",
        minWidth: 72,
        format: (row: ManufacturerModelRow) => renderValue(row.weelDrive, styles),
      },
      {
        id: "baseBeamTypeRoof",
        label: t("MANUFACTURER_MODELS.TABLE.WHEELBASE"),
        align: "center",
        minWidth: 96,
        format: (row: ManufacturerModelRow) => renderValue(row.baseBeamTypeRoof, styles),
      },
      {
        id: "chassisWeight",
        label: t("MANUFACTURER_MODELS.TABLE.CHASSIS_WEIGHT"),
        align: "center",
        minWidth: 110,
        format: (row: ManufacturerModelRow) => renderValue(row.chassisWeight, styles),
      },
      {
        id: "frontWeelBase",
        label: t("MANUFACTURER_MODELS.TABLE.FRONT_AXLE_DISTANCE"),
        align: "center",
        minWidth: 140,
        format: (row: ManufacturerModelRow) => renderValue(row.frontWeelBase, styles),
      },
      ...CHECKBOX_COLUMNS.map(({ id, labelKey }) => ({
        id,
        label: t(`MANUFACTURER_MODELS.TABLE.${labelKey}`),
        align: "center" as const,
        minWidth: 110,
        format: (row: ManufacturerModelRow) => renderCheckbox(styles),
      })),
    ],
    [styles, t]
  );

  return (
    <div className={styles.root} dir="rtl">
      <DataTable
        rows={rows}
        columns={columns}
        scrollClass="custom-scroll-bar"
        loading={loading && rows.length === 0}
        serialEnabled={false}
        isPointerCursor
        noDataLabel={t("MANUFACTURER_MODELS.EMPTY")}
        onRowClick={(row: ManufacturerModelRow) =>
          navigate(`/system-tables/model-per-manufacturer/${row.manufacturerModelID}`)
        }
      />
    </div>
  );
};

export default ModelTable;
